import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ControlsOf } from '../../utils/controls-of';
import { removeSlashesOnPaste } from '../../utils/input.helper';
import {
  ICustomerSites,
  ICustomerSitesContact,
  SiteOpeningHoursForm,
} from '../../interfaces/customer-sites';
import { isEqual, pick } from 'lodash';
import { MarkerPoint } from '../map-control/marker.point';
import { IVehicleLifeCycleProviderConnection } from '../../interfaces/vehicle-life-cycle/vehicle-life-cycle-provider-connection';
import { IVehicleLifeCycleCustomerSiteConnection } from '../../interfaces/vehicle-life-cycle/vehicle-life-cycle-customer-site-connection';
import {
  CustomerSitesService,
  ManualSearchAddress,
  PcaService,
  UserService,
  VehicleAddressService,
} from '../../services';
import { SiteOpeningHoursService } from '../../services/site-opening-hours.service';
import { VehicleLifecycleService } from '../../services/vehicle-lifecycle.service';
import { ConfirmationService, MessageService, OverlayPanel } from 'primeng';
import { NoEmptyString } from '../../interfaces/no-empty-string.validator';
import { add, startOfDay } from 'date-fns';
import { CustomerSitesOpeningTimesComponent } from './customer-sites-opening-times.component';
import { Message } from '../../classes/Message';
import { IVehicleAddress } from '../../interfaces/task-info';

interface CustomerSitesForm extends Omit<ICustomerSites, 'openingHours'> {}

@Component({
  selector: 'app-customer-sites-editor',
  templateUrl: './customer-sites-editor.component.html',
  styleUrls: ['./customer-sites-editor.component.css'],
})
export class CustomerSitesEditorComponent implements OnInit, OnChanges {
  @Input() site: ICustomerSites | null = null;
  @Input() visible: boolean = false;
  @Output() visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() updatedSite: EventEmitter<ICustomerSites> = new EventEmitter<ICustomerSites>();
  @ViewChild('siteHoursForm') siteHoursForm: OverlayPanel;
  @ViewChild('customerSitesOpeningTimes') openingTimes: CustomerSitesOpeningTimesComponent;
  @Input() source: CustomerSiteEditorSource;
  @Input() vehicleAddressSource: IVehicleAddress;
  @Input() contactInfo: ICustomerSitesContact;

  public pickupPhone1Error: string;
  public pickupPhone2Error: string;
  public dropoffPhone1Error: string;
  public dropoffPhone2Error: string;

  public prefilledFromBookingSummary: string = null;
  public editSitesForm: FormGroup<ControlsOf<CustomerSitesForm>>;

  public organisationId: number = 0;

  @Input() openingTimesForm: FormArray<FormGroup<SiteOpeningHoursForm>>;

  public get currentAddressValidated(): boolean {
    let comparisonProps: Array<keyof ICustomerSites> = [
      'businessName',
      'address1',
      'city',
      'postcode',
    ];
    return isEqual(
      pick(this._validatedAddress, comparisonProps),
      pick(this.editSitesForm?.value, comparisonProps),
    );
  }

  public get siteMarkerPoint(): MarkerPoint {
    if (!this.editSitesForm) {
      return null;
    }
    return new MarkerPoint(
      this.editSitesForm.controls.latitude.value,
      this.editSitesForm.controls.longitude.value,
    );
  }

  private _validatedAddress;

  public isUserImpersonated: boolean;

  vehicleLifeCycleManageProvidersVisible: boolean = false;
  vehicleLifeCycleProviderUpdateSaving: boolean = false;
  vehicleLifeCycleProviderConnections: IVehicleLifeCycleProviderConnection[] = [];
  vehicleLifeCycleCustomerSiteConnections: IVehicleLifeCycleCustomerSiteConnection[] = [];
  vehicleLifeCycleCustomerSiteProviderFormGroup: UntypedFormGroup;
  vehicleLifeCycleSelectedProvider: IVehicleLifeCycleProviderConnection = null;

  constructor(
    private userService: UserService,
    private csService: CustomerSitesService,
    private pcaService: PcaService,
    private vehicleAddressService: VehicleAddressService,
    private siteOpeningHoursService: SiteOpeningHoursService,
    private vehicleLifeCycleService: VehicleLifecycleService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
  ) {}

  ngOnInit() {
    this.prefilledFromBookingSummary = this.getPrefilledFromBookingSummary();

    this.userService.getCurrentUserOrg().then((res) => {
      if (res) {
        this.organisationId = res.id;
      }

      this.isUserImpersonating();

      this.pcaService.Init();

      this.initLifeCycleProviderForm();

      this.edit(this.site);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['site']) {
      const previousSite = changes['site'].previousValue;
      const currentSite = changes['site'].currentValue;

      if (!isEqual(previousSite, currentSite)) {
        this.edit(currentSite);
      }
    }
  }

  initForm() {
    let ctlPPhone1 = new FormControl<string>('', (validator: FormControl<string>) => {
      this.pickupPhone1Error = '';

      if (validator.value && !this.validMobileNumber(validator.value)) {
        let error = 'Invalid Mobile Number';
        this.pickupPhone1Error = error;

        return { valid: false, error: error };
      }

      return null;
    });

    let ctlPPhone2 = new FormControl<string>('', (validator: FormControl<string>) => {
      this.pickupPhone2Error = '';

      if (validator.value && !this.validPhoneNumber(validator.value)) {
        let error = 'Invalid Phone Number';
        this.pickupPhone2Error = error;

        return { valid: false, error: error };
      }

      return null;
    });

    let ctlDPhone1 = new FormControl<string>('', (validator: FormControl<string>) => {
      this.dropoffPhone1Error = '';

      if (validator.value && !this.validMobileNumber(validator.value)) {
        let error = 'Invalid Mobile Number';
        this.dropoffPhone1Error = error;

        return { valid: false, error: error };
      }

      return null;
    });

    let ctlDPhone2 = new FormControl<string>('', (validator: FormControl<string>) => {
      this.dropoffPhone2Error = '';

      if (validator.value && !this.validPhoneNumber(validator.value)) {
        let error = 'Invalid Phone Number';
        this.dropoffPhone2Error = error;

        return { valid: false, error: error };
      }

      return null;
    });

    const options = {
      id: new FormControl<number>(0),
      businessName: new FormControl<string>('', Validators.compose([Validators.maxLength(50)])),
      customerId: new FormControl<number>(this.organisationId),
      city: new FormControl<string>(
        '',
        Validators.compose([Validators.required, Validators.maxLength(50), NoEmptyString]),
      ),
      address1: new FormControl<string>(
        '',
        Validators.compose([Validators.required, Validators.maxLength(100), NoEmptyString]),
      ),
      address2: new FormControl<string>('', Validators.compose([Validators.maxLength(50)])),
      address3: new FormControl<string>('', Validators.compose([Validators.maxLength(50)])),
      dropoffEmail: new FormControl<string>(''),
      releaseCode: new FormControl<string>(''),
      dropoffNotification: new FormControl<boolean>(false),
      dropoffForename: new FormControl<string>(''),
      dropoffNotes: new FormControl<string>('', Validators.compose([Validators.maxLength(2000)])),
      dropoffPhone1: ctlDPhone1,
      pickupPhone2: ctlPPhone2,
      pickupNotification: new FormControl<boolean>(false),
      dropoffPhone2: ctlDPhone2,
      dropoffSurname: new FormControl<string>(''),
      latitude: new FormControl<number>(52),
      longitude: new FormControl<number>(-2),
      pickupEmail: new FormControl<string>(''),
      pickupForename: new FormControl<string>(''),
      pickupNotes: new FormControl<string>('', Validators.compose([Validators.maxLength(2000)])),
      pickupPhone1: ctlPPhone1,
      pickupSurname: new FormControl<string>(''),
      postcode: new FormControl<string>(
        '',
        Validators.compose([Validators.required, Validators.maxLength(50), NoEmptyString]),
      ),
      what3Words: new FormControl<string>(
        '',
        Validators.compose([
          Validators.maxLength(64),
          Validators.pattern(/^[a-zA-Z]+\.[a-zA-Z]+\.[a-zA-Z]+$/),
        ]),
      ),
      contactNo: new FormControl<string>('', Validators.compose([Validators.maxLength(50)])),
      siteName: new FormControl<string>(
        '',
        Validators.compose([Validators.required, Validators.maxLength(50), NoEmptyString]),
      ),
      disabled: new FormControl<boolean>(false),
      nearestLocationMatchEnabled: new FormControl<boolean>(false),
      vehicleLifeCycleMaxCapacity: new FormControl<number>(
        0,
        Validators.compose([Validators.required, Validators.pattern('^[0-9]*$')]),
      ),
      vehicleLifeCycleRefurbsOnSiteCount: new FormControl<number>(0),
    };

    this.editSitesForm = new FormGroup<ControlsOf<CustomerSitesForm>>(options);

    let startOfToday = startOfDay(new Date());

    const isSiteManager = this.source == CustomerSiteEditorSource.SiteManager;

    if (this.openingTimesForm == null) {
      this.openingTimesForm = this.siteOpeningHoursService.createForm(
        isSiteManager ? null : [],
        isSiteManager,
      );
    }
  }

  edit(sites: ICustomerSites) {
    this.initForm();
    let startOfToday = startOfDay(new Date());
    if (sites != null) {
      this.editSitesForm.patchValue(sites);
      this.openingTimesForm.patchValue(
        sites.openingHours.map((x) => ({
          close: x.close != null ? add(startOfToday, { minutes: x.close }) : null,
          open: x.open != null ? add(startOfToday, { minutes: x.open }) : null,
          dayOfWeek: x.dayOfWeek,
        })),
      );
      this.openingTimesForm.markAllAsTouched();
      this.openingTimesForm.markAsDirty();
      this._validatedAddress = sites;
    }

    if (
      sites == null &&
      this.vehicleAddressSource != null &&
      (this.source == CustomerSiteEditorSource.CreateMovementDropoff ||
        this.source == CustomerSiteEditorSource.CreateMovementPickup)
    ) {
      this.editSitesForm.patchValue({
        address1: this.vehicleAddressSource.addressLine1 ?? '',
        address2: this.vehicleAddressSource.addressLine2 ?? '',
        address3: this.vehicleAddressSource.addressLine3 ?? '',
        businessName: this.vehicleAddressSource.businessName ?? '',
        city: this.vehicleAddressSource.city ?? '',
        postcode: this.vehicleAddressSource.postcode ?? '',
        what3Words: this.vehicleAddressSource.what3Words ?? '',
        primaryAddress: this.vehicleAddressSource.primaryAddress ?? '',
        siteName: this.vehicleAddressSource.businessName ?? '',
      });
    }

    if (
      sites == null &&
      this.contactInfo != null &&
      this.source != CustomerSiteEditorSource.SiteManager
    ) {
      if (this.source == CustomerSiteEditorSource.CreateMovementPickup) {
        this.editSitesForm.patchValue({
          pickupForename: this.contactInfo.forename ?? '',
          pickupSurname: this.contactInfo.surname ?? '',
          pickupEmail: this.contactInfo.email ?? '',
          pickupPhone1: this.contactInfo.phone1 ?? '',
          pickupPhone2: this.contactInfo.phone2 ?? '',
          pickupNotes: this.contactInfo.notes ?? '',
          pickupNotification: !!this.contactInfo.notification,
        });
      } else if (this.source == CustomerSiteEditorSource.CreateMovementDropoff) {
        this.editSitesForm.patchValue({
          dropoffForename: this.contactInfo.forename ?? '',
          dropoffSurname: this.contactInfo.surname ?? '',
          dropoffEmail: this.contactInfo.email ?? '',
          dropoffPhone1: this.contactInfo.phone1 ?? '',
          dropoffPhone2: this.contactInfo.phone2 ?? '',
          dropoffNotes: this.contactInfo.notes ?? '',
          dropoffNotification: !!this.contactInfo.notification,
        });
      }
    }
  }

  saveSite() {
    let site: ICustomerSites = {
      ...this.editSitesForm.getRawValue(),
      openingHours: this.siteOpeningHoursService.getOpeningHours(this.openingTimesForm),
    };
    this.csService.saveSites(site, site.id == 0).then((response) => {
      this.csService.getSite(response).then((site) => {
        this.updatedSite.emit(site);
        this.closeDialog();
        if (site.id == 0) {
          this.messageService.add(Message.Success("You've successfully saved this site"));
        } else {
          this.messageService.add(Message.Success("You've successfully updated this site"));
        }
      });
    });
  }

  searchAddress() {
    let manualEntry: ManualSearchAddress = {
      BusinessName: this.editSitesForm.controls.businessName.value,
      City: this.editSitesForm.controls.city.value,
      PostalCode: this.editSitesForm.controls.postcode.value,
      What3Words: this.editSitesForm.controls.what3Words.value,
      Line1: this.editSitesForm.controls.address1.value,
      Line2: this.editSitesForm.controls.address2.value,
      Line3: this.editSitesForm.controls.address3.value,
    };

    this.vehicleAddressService
      .GetFromManualEntry(manualEntry)
      .then((x) => {
        if (!x) {
          return;
        }

        this.editSitesForm.controls.latitude.setValue(x.latitude);
        this.editSitesForm.controls.longitude.setValue(x.longitude);

        if (x.addressLine1) {
          this.editSitesForm.controls.address1.setValue(x.addressLine1);
        }

        this.editSitesForm.controls.city.setValue(x.city);
        this.editSitesForm.controls.postcode.setValue(x.postcode);

        this._validatedAddress = {
          address1: this.editSitesForm.controls.address1.value,
          city: x.city,
          businessName: x.businessName,
          postcode: x.postcode,
        };
      })
      .catch((ex) => {
        return;
      });
  }

  validPhoneNumber(phoneNumber: string) {
    return phoneNumber.match(
      /^(((\+44\s?\d{4}|\(?0\d{4}\)?)\s?\d{3}\s?\d{3})|((\+44\s?\d{3}|\(?0\d{3}\)?)\s?\d{3}\s?\d{4})|((\+44\s?\d{2}|\(?0\d{2}\)?)\s?\d{4}\s?\d{4}))(\s?\#(\d{4}|\d{3}))?$/,
    );
  }

  validMobileNumber(phoneNumber: string) {
    return phoneNumber.match(
      /^(((\+44\s?7\d{3}|\(?07\d{3}\)?)\s?\d{3}\s?\d{3})|((\+44\s?7\d{2}|\(?07\d{2}\)?)\s?\d{3}\s?\d{4})|((\+44\s?7\d{1}|\(?07\d{1}\)?)\s?\d{4}\s?\d{4}))?$/,
    );
  }

  onManualCoordinateChange(markerPoint: MarkerPoint) {
    if (
      markerPoint.latitude != this.editSitesForm.controls.latitude.value &&
      markerPoint.longitude != this.editSitesForm.controls.longitude.value
    ) {
      this.editSitesForm.controls.latitude.setValue(markerPoint.latitude);
      this.editSitesForm.controls.longitude.setValue(markerPoint.longitude);
      this.editSitesForm.markAsDirty();
    }
  }

  closeSiteHoursForm() {
    this.siteHoursForm.hide();
  }

  onHide() {
    this.openingTimes.closeClick();
    this.closeDialog();
  }

  isUserImpersonating() {
    this.userService.isUserImpersonating().then((res) => {
      let resultData: any;
      resultData = res;
      if (resultData != null) {
        this.isUserImpersonated = resultData.isImpersonating;
      } else {
        this.isUserImpersonated = false;
      }
    });
  }

  getLifeCycleProviderConnections(): void {
    this.vehicleLifeCycleService.getProviderConnections(this.organisationId).then((res) => {
      this.vehicleLifeCycleProviderConnections = res;
      this.initLifeCycleProviderForm();
    });
  }

  getLifeCycleCustomerSiteConnections(): void {
    this.vehicleLifeCycleService
      .getCustomerSitesProviderConnections(this.editSitesForm?.get('id').value)
      .then((res) => {
        this.vehicleLifeCycleCustomerSiteConnections = res;
        this.getLifeCycleProviderConnections();
      });
  }

  initLifeCycleProviderForm(
    siteConfiguration: IVehicleLifeCycleCustomerSiteConnection = null,
  ): void {
    this.vehicleLifeCycleCustomerSiteProviderFormGroup = new UntypedFormGroup({
      id: new UntypedFormControl(siteConfiguration?.id ?? 0),
      providerConnectionId: new UntypedFormControl(siteConfiguration?.providerConnectionId ?? 0),
      siteId: new UntypedFormControl(this.editSitesForm?.get('id').value ?? 0),
      externalSiteId: new UntypedFormControl(
        siteConfiguration?.externalSiteId ?? '',
        Validators.required,
      ),
    });
  }

  configureLifeCycleCustomerSitesProviders(): void {
    this.vehicleLifeCycleSelectedProvider = null;
    this.getLifeCycleCustomerSiteConnections();
    this.vehicleLifeCycleManageProvidersVisible = true;
  }

  selectProviderConfiguration(connection: IVehicleLifeCycleProviderConnection): void {
    let existingProviderCustomerSiteConfiguration =
      this.vehicleLifeCycleCustomerSiteConnections.find(
        (x) => x.providerConnectionId == connection.id,
      );

    if (existingProviderCustomerSiteConfiguration) {
      this.initLifeCycleProviderForm(existingProviderCustomerSiteConfiguration);
    } else {
      this.initLifeCycleProviderForm();
      this.vehicleLifeCycleCustomerSiteProviderFormGroup
        .get('providerConnectionId')
        .setValue(connection.id);
    }
  }

  saveLifeCycleProviderConnection(): void {
    if (
      this.vehicleLifeCycleCustomerSiteProviderFormGroup.get('providerConnectionId').value ==
        null ||
      this.vehicleLifeCycleCustomerSiteProviderFormGroup.get('providerConnectionId').value == 0
    ) {
      this.messageService.add(Message.Warning('Please select a provider from the list'));
      return;
    }

    this.vehicleLifeCycleProviderUpdateSaving = true;
    this.vehicleLifeCycleService
      .addUpdateCustomerSiteProviderConnection(
        this.vehicleLifeCycleCustomerSiteProviderFormGroup.getRawValue(),
      )
      .then((res) => {
        this.vehicleLifeCycleCustomerSiteConnections = res;
        this.vehicleLifeCycleProviderUpdateSaving = false;
        this.messageService.add(Message.Success('Site updated successfully'));
      })
      .catch((err) => {
        this.vehicleLifeCycleProviderUpdateSaving = false;
      });
  }

  closeDialog() {
    this.visibleChange.emit(false);
  }

  getDialogHeading(): string {
    switch (this.source) {
      case CustomerSiteEditorSource.SiteManager:
        return 'Customer Site Details';
      case CustomerSiteEditorSource.CreateMovementPickup:
      case CustomerSiteEditorSource.CreateMovementDropoff:
        return 'Add to Saved Sites';
      default:
        return 'Customer Site Details';
    }
  }

  getSaveButtonLabel(): string {
    switch (this.source) {
      case CustomerSiteEditorSource.SiteManager:
        return this.editSitesForm.get('id').value == 0 ? 'Add Site' : 'Update Site';
      case CustomerSiteEditorSource.CreateMovementPickup:
      case CustomerSiteEditorSource.CreateMovementDropoff:
        return 'Add to Saved Sites';
      default:
        return 'Save';
    }
  }

  clearContactConfirm(isPickup: boolean) {
    this.confirmationService.confirm({
      message: 'Are you sure you want to clear the contact information?',
      header: 'Confirmation',
      accept: () => {
        this.clearContactInfo(isPickup);
      },
    });
  }

  clearContactInfo(isPickup: boolean) {
    if (isPickup) {
      this.editSitesForm.patchValue({
        pickupForename: '',
        pickupSurname: '',
        pickupEmail: '',
        pickupPhone1: '',
        pickupPhone2: '',
        pickupNotes: '',
        pickupNotification: false,
      });
    } else {
      this.editSitesForm.patchValue({
        dropoffForename: '',
        dropoffSurname: '',
        dropoffEmail: '',
        dropoffPhone1: '',
        dropoffPhone2: '',
        dropoffNotes: '',
        dropoffNotification: false,
      });
    }
  }

  private getPrefilledFromBookingSummary(): string {
    const isBookingSource =
      this.source == CustomerSiteEditorSource.CreateMovementDropoff ||
      this.source == CustomerSiteEditorSource.CreateMovementPickup;

    if (this.site != null || !isBookingSource) {
      return null;
    }

    const hasContactDetails = Object.values(this.contactInfo ?? {}).some(
      (value) => typeof value === 'string' && value.trim().length > 0,
    );

    const hasOpeningTimes = (this.openingTimesForm?.controls ?? []).some(
      (row) =>
        row.controls.open.disabled ||
        row.controls.close.disabled ||
        row.controls.open.value != null ||
        row.controls.close.value != null,
    );

    if (hasContactDetails && hasOpeningTimes) {
      return 'Contact details and opening times';
    }

    if (hasContactDetails) {
      return 'Contact details';
    }

    if (hasOpeningTimes) {
      return 'Opening times';
    }

    return null;
  }

  protected readonly CustomerSiteEditorSource = CustomerSiteEditorSource;
  protected readonly removeSlashesOnPaste = removeSlashesOnPaste;
}

export enum CustomerSiteEditorSource {
  SiteManager = 1,
  CreateMovementPickup = 2,
  CreateMovementDropoff = 3,
}
